/**
 * Сетевой слой трека — единственное место, откуда клиент обращается к серверу.
 *
 * Слой, расползшийся по экранам, нельзя ни подменить в тесте целиком, ни
 * прочитать глазами за один заход, а шаг ручного чек-листа занят именно этим.
 * Правило то же, что решение `D27` дало треку Android, и живёт оно здесь.
 *
 * Базовый адрес приходит переменной сборщика, и **умолчания у неё нет**
 * (решение `D36`): клиент, молча ушедший на адрес самой страницы, выглядит
 * работающим ровно до выкладки. Адреса литералом в треке нет ни одного.
 *
 * Токен живёт в памяти вкладки (решение `D28`): ни хранилища, ни cookie в
 * треке нет, перезагрузка страницы — повторный вход.
 *
 * Бизнес-правил здесь нет и быть не может: ни таблицы «роль → что можно», ни
 * перечня разрешённых переходов, ни отбора ленты. Отказ опознаётся машинным
 * кодом конверта — не текстом сообщения, который переводится на язык запроса,
 * и не транспортным статусом в одиночку.
 *
 * ПЕРЕВОРОТ блока `WEB-1.1` (спецификация §7): подписи входа принимают **канал
 * из двух** и согласие с условиями. Форма тела задана контрактом: «Канал ровно
 * один из двух. Какой именно — выбирает клиент; проверку „хотя бы один канал
 * указан“ выполняет сервер». Поэтому канал выражен объединением, а не парой
 * необязательных полей: пара позволила бы отправить оба ключа сразу или ни
 * одного, и поймал бы это только сервер.
 *
 * Проверок формата телефона и почты здесь нет ни одной, и это не упущение:
 * клиент решает, **в какое поле тела** положить введённое, а годность значения
 * решает сервер. Своя проверка разошлась бы с серверной молча.
 */

import { errorCode, errorDetails, errorMessage, isErrorEnvelope, unwrap } from './contract';
import type {
  BodyTypeInfo,
  CargoBodyInput,
  CargoListing,
  CargoListingInput,
  CargoOffer,
  CargoSlot,
  City,
  Company,
  CompanyCreateRequest,
  CompanyMember,
  CurrentUserData,
  DataEnvelope,
  Document,
  DocumentLink,
  ErrorCode,
  ErrorDetail,
  Invitation,
  MemberPermissions,
  MembershipRole,
  PageEnvelope,
  Permission,
  PublicTracking,
  RepairStateChangeRequest,
  ServiceRecord,
  ServiceRecordCreateRequest,
  Session,
  User,
  Vehicle,
  VehicleCreateRequest,
  VinClaim,
  VinClaimCreateRequest,
  Work,
} from './contract';

/** Имя переменной сборщика. Называется в треке ровно один раз — здесь. */
export const API_BASE_URL_VARIABLE = 'VITE_API_BASE_URL';

/** Машинный код, по которому вкладка возвращается на вход. */
export const UNAUTHENTICATED_CODE: ErrorCode = 'UNAUTHENTICATED';

/**
 * Базовый адрес сервера из окружения сборщика.
 *
 * Пустое и пробельное значение — то же, что отсутствие: переменная, заданная
 * пустой строкой, отличается от незаданной только в отчёте сборщика.
 */
export function readApiBaseUrl(env: Readonly<Record<string, unknown>>): string {
  const declared = env[API_BASE_URL_VARIABLE];

  if (typeof declared !== 'string' || declared.trim() === '') {
    throw new Error(
      `Не задан адрес сервера: переменная ${API_BASE_URL_VARIABLE} пуста или отсутствует. `
      + 'Умолчания у неё нет намеренно — задайте её и запустите приложение снова.',
    );
  }

  return declared.trim();
}

/**
 * Отказ обращения к API.
 *
 * Машинный код есть только у отказа, пришедшего конвертом контракта. Обрыв
 * связи, стоковая страница фреймворка и посредник, ответивший по-своему, дают
 * тот же отказ без кода: выдумывать за них код значит решать по транспорту.
 */
export class ApiFailure extends Error {
  readonly code: ErrorCode | null;
  readonly requestId: string | null;
  /**
   * Сообщения сервера по полям формы — форма контракта, а не ветвление.
   *
   * Экран кладёт `detail.message` рядом с элементом, чьё имя равно
   * `detail.field`, и ни одного имени поля при этом не знает. Разбор `details`
   * ради решения «что делать дальше» был бы переносом правила в клиент.
   */
  readonly details: readonly ErrorDetail[];
  /**
   * Текст отказа, написанный сервером, — для показа, а не для решения.
   *
   * Экран показывает его человеку, когда отказ осмысленный: правило «нельзя
   * обезоружить единственного директора» знает сервер, и своими словами его
   * пересказал бы клиент, заведя правило (долг `TD-67`). Ветвления по этому
   * тексту нет ни одного — решает машинный код.
   *
   * `null` означает «сервер не сказал ничего»: отказ пришёл без конверта
   * контракта — обрыв связи, стоковая страница фреймворка, посредник.
   *
   * Имя не `message` намеренно: `message` у отказа уже есть — его объявляет
   * `Error`, и там стоит строка самого клиента. Два разных текста под одним
   * именем спорили бы молча.
   */
  readonly reason: string | null;

  constructor(
    code: ErrorCode | null,
    requestId: string | null,
    details: readonly ErrorDetail[] = [],
    reason: string | null = null,
  ) {
    super('Обращение к API отклонено');

    this.name = 'ApiFailure';
    this.code = code;
    this.requestId = requestId;
    this.details = details;
    this.reason = reason;
  }
}

/** Что отдаёт подтверждение кода: токен и профиль, пришедшие одним ответом. */
export interface AuthSession {
  readonly token: string;
  readonly user: User;
}

export interface ApiClientOptions {
  readonly baseUrl: string;
}

/** Канал одноразового кода. Ровно один из двух — выбирает клиент. */
export type AuthChannel = 'phone' | 'email';

/**
 * Тело запроса кода: **один** ключ канала и второго нет вовсе.
 *
 * Объединение, а не два необязательных поля: форма, допускающая оба ключа
 * сразу, отличается от контракта ровно тем, что расхождение с ним ловит только
 * сервер.
 */
export type AuthCodeInput = { readonly phone: string } | { readonly email: string };

/**
 * Тело подтверждения: тот же единственный ключ канала, код и согласие.
 *
 * Согласие объявлено значением `true`, а не `boolean`: «не отмечено» — это
 * **отсутствие ключа**, а не ложь. Кто новый пользователь, а кто прежний,
 * решает сервер, и отправленное «нет» было бы утверждением клиента о том,
 * чего он не знает.
 */
export type AuthVerifyInput =
  | { readonly phone: string; readonly code: string; readonly terms_accepted?: true }
  | { readonly email: string; readonly code: string; readonly terms_accepted?: true };

/**
 * Тело подтверждения второго канала: тот же единственный ключ канала и код.
 *
 * Согласия здесь нет: оно принадлежит входу, создающему аккаунт, а второй
 * канал добавляет уже вошедший. Форма тела — та же, что у входа, потому что
 * так её объявляет контракт.
 */
export type ChannelConfirmInput =
  | { readonly phone: string; readonly code: string }
  | { readonly email: string; readonly code: string };

/**
 * Тело приглашения: роль и **один** ключ канала.
 *
 * Объединение, а не два необязательных поля, — по той же причине, что и у
 * входа: форма, допускающая оба ключа сразу, расходится с контрактом так, что
 * поймает это только сервер. Сгенерированный тип тела здесь не годится:
 * `anyOf` контракта вырождается в `unknown`.
 */
export type InvitationInput =
  | { readonly phone: string; readonly role: MembershipRole }
  | { readonly email: string; readonly role: MembershipRole };

/**
 * Тело формы объявления о грузе — поля `CargoListingInput` и ни одного сверх.
 *
 * От схемы оно отличается ровно одним: незаполненное поле уходит `null`, а не
 * выдуманным значением. Обязательность, пределы и даты судит сервер (`422` по
 * полю), и ноль вместо пустого веса был бы утверждением клиента о том, чего
 * человек не вводил.
 */
export type CargoBodySubmission = { readonly [K in keyof CargoBodyInput]: CargoBodyInput[K] | null };
export type CargoListingSubmission = {
  readonly [K in Exclude<keyof CargoListingInput, 'body'>]: CargoListingInput[K] | null;
} & { readonly body: CargoBodySubmission };

export interface ApiClient {
  /** Токен вкладки. `null` — вход не пройден либо токен отвергнут сервером. */
  readonly token: string | null;
  /**
   * Язык гостя: уходит заголовком **только** на обращениях без токена.
   *
   * Язык владельца токена определяет профиль (контракт), и второй источник
   * спорил бы с первым.
   */
  useLocale(locale: User['locale']): void;
  requestAuthCode(input: AuthCodeInput): Promise<void>;
  verifyAuthCode(input: AuthVerifyInput): Promise<AuthSession>;
  getCurrentUser(): Promise<CurrentUserData>;
  updateCurrentUser(input: { locale: User['locale'] }): Promise<CurrentUserData>;
  createCompany(input: CompanyCreateRequest): Promise<Company>;
  listCompanyMembers(companyId: string): Promise<readonly CompanyMember[]>;
  listWorks(options?: { cursor?: string }): Promise<PageEnvelope<Work>>;

  // Операции кабинета — блок `WEB-1.2`. Перечень слоя закрыт целиком и
  // сверяется с описью операций контракта: имени, которого контракт не
  // объявлял, здесь нет, а `closeMembership` отсутствует намеренно —
  // требования на выход из фирмы нет ни одного, и заготовка под него была бы
  // заглушкой.
  /** Видимые сессии владельца токена. Признак текущей приходит полем. */
  listSessions(): Promise<readonly Session[]>;
  /** Завершение сессии, включая текущую: токен отзывается немедленно. */
  terminateSession(sessionId: string): Promise<void>;
  /** Код на второй канал. Форма тела — та же, что у входа. */
  requestChannelCode(input: AuthCodeInput): Promise<void>;
  /** Подтверждение второго канала. Наружу идёт обновлённый профиль из ответа. */
  confirmChannel(input: ChannelConfirmInput): Promise<CurrentUserData>;
  createInvitation(companyId: string, input: InvitationInput): Promise<Invitation>;
  listCompanyInvitations(companyId: string): Promise<readonly Invitation[]>;
  /**
   * Отзыв приглашения. Наружу не идёт ничего: контракт объявляет `204` **без
   * тела**, и объявленный тип обязан совпадать с ответом контракта.
   */
  revokeInvitation(companyId: string, invitationId: string): Promise<void>;
  resendInvitation(companyId: string, invitationId: string): Promise<Invitation>;
  /** Слои-источники: роль-ярлык и два персональных слоя, как их отдал сервер. */
  listMemberPermissions(companyId: string, membershipId: string): Promise<MemberPermissions>;
  grantMemberPermission(companyId: string, membershipId: string, right: Permission): Promise<MemberPermissions>;
  revokeMemberGrant(companyId: string, membershipId: string, right: Permission): Promise<void>;
  denyMemberPermission(companyId: string, membershipId: string, right: Permission): Promise<MemberPermissions>;
  revokeMemberDeny(companyId: string, membershipId: string, right: Permission): Promise<void>;
  listCompanyDocuments(companyId: string): Promise<readonly Document[]>;
  listMemberDocuments(companyId: string, membershipId: string): Promise<readonly Document[]>;
  /** Временная подписанная ссылка. Годность её решает сервер. */
  issueDocumentLink(documentId: string): Promise<DocumentLink>;

  // Операции домена транспорта — блок `WEB-1.3`. Перечень слоя по-прежнему
  // закрыт целиком и сверяется с описью операций контракта. Операций решения
  // по заявке, правки и удаления сервисной записи здесь нет и появиться не
  // может — контракт их не объявляет вовсе: журнал только пополняется, а
  // решение по заявке — предмет Admin.
  /** Реестр транспорта фирмы — каждая запись как пришла, выведенные тоже. */
  listCompanyVehicles(companyId: string): Promise<readonly Vehicle[]>;
  /**
   * Добавление транспорта. Повтор своего VIN возвращает ту же запись со
   * статусом 200 — идемпотентность по естественному ключу, заголовок ей не
   * нужен; клиент повтора не подавляет и формата VIN не проверяет.
   */
  addCompanyVehicle(companyId: string, input: VehicleCreateRequest): Promise<Vehicle>;
  /** Вывод из реестра: запись закрывается датой. `204` без тела, повтор законен. */
  withdrawCompanyVehicle(companyId: string, vehicleId: string): Promise<void>;
  /**
   * Перевод состояния цикла ремонта. Перечень переходов живёт на сервере:
   * клиент шлёт выбранное и рисует ответ — тот несёт запись целиком.
   */
  changeVehicleRepairState(companyId: string, vehicleId: string, input: RepairStateChangeRequest): Promise<Vehicle>;
  /** Сервисная история — журнал только пополняется, вывод её не трогает. */
  listVehicleServiceRecords(companyId: string, vehicleId: string): Promise<readonly ServiceRecord[]>;
  /**
   * Сервисная запись. Идемпотентность — заголовком: ключ рождается здесь на
   * каждую отправку заново, две одинаковые записи с разными ключами законны.
   */
  addVehicleServiceRecord(companyId: string, vehicleId: string, input: ServiceRecordCreateRequest): Promise<ServiceRecord>;
  /** Заявки обеих сторон одним списком: сторона видна полем direction. */
  listCompanyVinClaims(companyId: string): Promise<readonly VinClaim[]>;
  /**
   * Заявка на освобождение занятого VIN — реквизитом техпаспорта строкой.
   * Повтор живой заявки возвращает её же со статусом 200; клиент не подавляет.
   */
  createVinClaim(companyId: string, input: VinClaimCreateRequest): Promise<VinClaim>;
  /** Возражение держателя. Окно считает сервер: после окна — 422 от него. */
  objectVinClaim(companyId: string, claimId: string): Promise<VinClaim>;

  // Операция блока `WEB-3.2` — страница публичного отслеживания по ссылке с
  // PIN. Живёт в том же интерфейсе слоя: второй путь отправки рядом с общим
  // означал бы второе место, где решается, какие заголовки уходят.
  /**
   * Просмотр работы по публичной ссылке. Входа нет: слой страницы создаётся
   * без токена, и язык уходит заголовком гостя. Неверный PIN, запирание и
   * «ссылка не действует» — отказы сервера с машинным кодом; попыток клиент не
   * считает и ничего не запирает сам.
   */
  viewPublicTracking(token: string, pin: string): Promise<PublicTracking>;

  // Операции блока `WEB-2.1` — кабинет логиста: объявления о грузе фирмы,
  // входящие offer'ы и доска слотов, плюс два справочника формы. Перечень слоя
  // по-прежнему закрыт целиком и сверяется с описью операций контракта;
  // операции чтения работы здесь нет намеренно — доска показывает номер работы
  // из слота и работ не запрашивает (решение формы Р3 А).
  /** Подсказка городов по строке — в порядке ответа, именами на языке ответа. */
  listCities(query: { q: string }): Promise<readonly City[]>;
  /**
   * Справочник кузовов: тип, подтипы и применимые параметры. Матрица
   * применимости живёт на сервере, и у клиента своей нет.
   */
  listBodyTypes(): Promise<readonly BodyTypeInfo[]>;
  /**
   * Публикация объявления. Ключ идемпотентности приносит форма: повтор
   * отправки до ответа обязан уйти **тем же** ключом (решение формы Б5), а
   * знает о повторе только она. Рождается ключ по-прежнему в одном месте —
   * `newIdempotencyKey` этого файла.
   */
  createCargoListing(companyId: string, input: CargoListingSubmission, idempotencyKey: string): Promise<CargoListing>;
  /** Объявления фирмы во всех статусах, новые сверху, — страницей курсора. */
  listCompanyCargoListings(companyId: string, options?: { cursor?: string }): Promise<PageEnvelope<CargoListing>>;
  /** Объявление целиком: км, транзит, `manageable` и цену считает сервер. */
  getCargoListing(listingId: string): Promise<CargoListing>;
  /** Правка объявления целиком — та же форма тела, без ключа идемпотентности. */
  updateCargoListing(listingId: string, input: CargoListingSubmission): Promise<CargoListing>;
  /** Закрытие объявления. Повтор над закрытым сервер принимает тем же телом. */
  closeCargoListing(listingId: string): Promise<CargoListing>;
  /** Offer'ы объявления во всех статусах, новые сверху, — страницей курсора. */
  listCargoListingOffers(listingId: string, options?: { cursor?: string }): Promise<PageEnvelope<CargoOffer>>;
  /** Одобрение offer'а. Истёк ли он и хватает ли слотов, решает сервер. */
  acceptCargoOffer(offerId: string): Promise<CargoOffer>;
  /** Отклонение offer'а — без тела: причины CR-004 §5 не берёт. */
  declineCargoOffer(offerId: string): Promise<CargoOffer>;
  /** Слоты заказа в порядке ответа: номер, статус, offer и работа слота. */
  listCargoSlots(listingId: string): Promise<readonly CargoSlot[]>;
}

const AUTH_CODE_PATH = '/v1/auth/request-code';
const AUTH_VERIFY_PATH = '/v1/auth/verify';
const CURRENT_USER_PATH = '/v1/me';
const COMPANIES_PATH = '/v1/companies';
const COMPANY_MEMBERS_SEGMENT = '/members';
const WORKS_PATH = '/v1/works';

// Адреса кабинета — блок `WEB-1.2`. Названы здесь по одному разу, как и
// прежние: второй экземпляр формы адреса перестанет разбираться в тот день,
// когда форма изменится.
const SESSIONS_PATH = '/v1/sessions';
const CHANNEL_CODE_PATH = '/v1/me/channels/request-code';
const CHANNEL_CONFIRM_PATH = '/v1/me/channels/confirm';
const DOCUMENTS_PATH = '/v1/documents';
const DOCUMENTS_SEGMENT = '/documents';
const INVITATIONS_SEGMENT = '/invitations';
const RIGHTS_SEGMENT = '/permissions';

// Адреса домена транспорта — блок `WEB-1.3`. Названы здесь по одному разу,
// рядом с прежними, и по той же причине.
const VEHICLES_SEGMENT = '/vehicles';
const REPAIR_STATE_SEGMENT = '/repair-state';
const SERVICE_RECORDS_SEGMENT = '/service-records';
const VIN_CLAIMS_SEGMENT = '/vin-claims';
const OBJECTION_SEGMENT = '/objection';

// Адрес просмотра по публичной ссылке — блок `WEB-3.2`. Назван здесь один раз.
const PUBLIC_TRACKING_PATH = '/v1/public/tracking';

// Адреса кабинета логиста — блок `WEB-2.1`. Названы здесь по одному разу.
const CITIES_PATH = '/v1/cities';
const BODY_TYPES_PATH = '/v1/body-types';
const CARGO_LISTINGS_PATH = '/v1/cargo-listings';
const CARGO_LISTINGS_SEGMENT = '/cargo-listings';
const CARGO_OFFERS_PATH = '/v1/cargo-offers';
const OFFERS_SEGMENT = '/offers';
const SLOTS_SEGMENT = '/slots';

/** Заголовок языка гостя. Называется в треке один раз — здесь. */
const LANGUAGE_HEADER = 'accept-language';

/** Заголовок идемпотентности создающей операции. Назван один раз — здесь. */
const IDEMPOTENCY_HEADER = 'Idempotency-Key';

/**
 * Ключ идемпотентности — **единственное** место его рождения.
 *
 * Ключ рождается в момент отправки, и на новую отправку рождается новый:
 * переиспользованный ключ склеил бы две разные записи, а две одинаковые
 * записи законны — так объявляет контракт. Придумывает ключ клиент.
 */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

function withoutTrailingSlash(value: string): string {
  let trimmed = value.trim();

  while (trimmed.endsWith('/')) {
    trimmed = trimmed.slice(0, -1);
  }

  return trimmed;
}

/** Адрес фирмы. Единственное место, где он собирается. */
function companyPath(companyId: string): string {
  return `${COMPANIES_PATH}/${encodeURIComponent(companyId)}`;
}

/** Адрес объявления о грузе. Тот же единственный сбор. */
function cargoListingPath(listingId: string): string {
  return `${CARGO_LISTINGS_PATH}/${encodeURIComponent(listingId)}`;
}

/** Адрес offer'а на груз. Тот же единственный сбор. */
function cargoOfferPath(offerId: string): string {
  return `${CARGO_OFFERS_PATH}/${encodeURIComponent(offerId)}`;
}

/**
 * Адрес с курсором страницы. Курсор непрозрачен: он уходит как пришёл, и
 * разбирать его клиенту нечем.
 */
function withCursor(path: string, cursor?: string): string {
  if (cursor === undefined) {
    return path;
  }

  return `${path}?${new URLSearchParams({ cursor }).toString()}`;
}

/** Адрес записи членства внутри фирмы. Тот же единственный сбор. */
function membershipPath(companyId: string, membershipId: string): string {
  return `${companyPath(companyId)}${COMPANY_MEMBERS_SEGMENT}/${encodeURIComponent(membershipId)}`;
}

/** Адрес записи реестра транспорта внутри фирмы. Тот же единственный сбор. */
function vehiclePath(companyId: string, vehicleId: string): string {
  return `${companyPath(companyId)}${VEHICLES_SEGMENT}/${encodeURIComponent(vehicleId)}`;
}

/** Адрес заявки на освобождение VIN внутри фирмы. Тот же единственный сбор. */
function vinClaimPath(companyId: string, claimId: string): string {
  return `${companyPath(companyId)}${VIN_CLAIMS_SEGMENT}/${encodeURIComponent(claimId)}`;
}

/**
 * Адрес слоёв прав записи членства.
 *
 * Хвост слоя приходит значением: два слоя различаются одним сегментом, и
 * второй строитель рядом означал бы две формы одного адреса.
 */
function memberRightsPath(companyId: string, membershipId: string, tail: string): string {
  return `${membershipPath(companyId, membershipId)}${RIGHTS_SEGMENT}${tail}`;
}

async function readPayload(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const origin = withoutTrailingSlash(options.baseUrl);

  let token: string | null = null;
  let locale: User['locale'] | null = null;

  function failureOf(payload: unknown): ApiFailure {
    const code = errorCode(payload);
    const requestId = isErrorEnvelope(payload) ? payload.error.request_id : null;

    if (code === UNAUTHENTICATED_CODE) {
      // Вкладка, оставшаяся с отвергнутым токеном, повторяет отказ на каждом
      // действии, и «возврат на вход» выглядит работающим, не будучи им.
      token = null;
    }

    return new ApiFailure(code, requestId, errorDetails(payload), errorMessage(payload));
  }

  async function send<T>(
    path: string,
    method: string,
    payload?: unknown,
    extraHeaders?: Readonly<Record<string, string>>,
  ): Promise<T> {
    const headers: Record<string, string> = { accept: 'application/json', ...extraHeaders };
    const init: RequestInit = { method, headers };

    if (token !== null) {
      headers.authorization = `Bearer ${token}`;
    } else if (locale !== null) {
      // Заголовок языка — только у гостя. Контракт говорит дословно: «заголовок
      // продолжает действовать только для неаутентифицированных запросов»,
      // потому что язык владельца токена определяет профиль.
      headers[LANGUAGE_HEADER] = locale;
    }

    if (payload !== undefined) {
      headers['content-type'] = 'application/json';
      init.body = JSON.stringify(payload);
    }

    let response: Response;

    try {
      response = await fetch(`${origin}${path}`, init);
    } catch {
      // Обрыв связи приходит тем же отказом: необработанное исключение
      // транспорта оставляет на экране белое пятно вместо состояния отказа.
      throw new ApiFailure(null, null);
    }

    const body = await readPayload(response);

    if (!response.ok) {
      throw failureOf(body);
    }

    return body as T;
  }

  function worksPath(cursor?: string): string {
    if (cursor === undefined) {
      return WORKS_PATH;
    }

    return `${WORKS_PATH}?${new URLSearchParams({ cursor }).toString()}`;
  }

  return {
    get token(): string | null {
      return token;
    },

    useLocale(chosen: User['locale']): void {
      locale = chosen;
    },

    /**
     * Запрос кода подтверждения.
     *
     * Наружу не отдаётся ничего: срок кода и срок повторной отправки приходят
     * в ответе, но поведения вокруг них у блока нет, а число на экране без
     * поведения закрывает требование на вид.
     */
    async requestAuthCode(input: AuthCodeInput): Promise<void> {
      await send<unknown>(AUTH_CODE_PATH, 'POST', input);
    },

    async verifyAuthCode(input: AuthVerifyInput): Promise<AuthSession> {
      const envelope = await send<DataEnvelope<AuthSession>>(AUTH_VERIFY_PATH, 'POST', input);
      const session = unwrap(envelope);

      token = session.token;

      return session;
    },

    /** Профиль вместе с членствами: имя фирмы приезжает вложением. */
    async getCurrentUser(): Promise<CurrentUserData> {
      return unwrap(await send<DataEnvelope<CurrentUserData>>(CURRENT_USER_PATH, 'GET'));
    },

    /**
     * Смена языка вошедшего. Наружу отдаётся ответ сервера целиком: источник
     * истины — профиль, а не выбранное значение.
     */
    async updateCurrentUser(input: { locale: User['locale'] }): Promise<CurrentUserData> {
      return unwrap(
        await send<DataEnvelope<CurrentUserData>>(CURRENT_USER_PATH, 'PATCH', input),
      );
    },

    async createCompany(input: CompanyCreateRequest): Promise<Company> {
      return unwrap(await send<DataEnvelope<Company>>(COMPANIES_PATH, 'POST', input));
    },

    /**
     * Состав фирмы, чей идентификатор дан адресом.
     *
     * Ни отбора, ни пересортировки: из чего складывается действующее участие —
     * правило сервера, и копия условия здесь разошлась бы с ним молча.
     */
    async listCompanyMembers(companyId: string): Promise<readonly CompanyMember[]> {
      const path = `${COMPANIES_PATH}/${encodeURIComponent(companyId)}${COMPANY_MEMBERS_SEGMENT}`;

      return unwrap(await send<DataEnvelope<CompanyMember[]>>(path, 'GET'));
    },

    /**
     * Страница ленты в форме контракта: работы и метаданные курсора.
     *
     * Ни отбора, ни пересортировки: порядок задаёт сервер тем же курсором,
     * которым нарезал страницы, а правила видимости нет ни у кого.
     */
    async listWorks(options?: { cursor?: string }): Promise<PageEnvelope<Work>> {
      return await send<PageEnvelope<Work>>(worksPath(options?.cursor), 'GET');
    },

    /**
     * Видимые сессии владельца токена.
     *
     * Признак «текущая» приходит полем ответа и клиентом не вычисляется:
     * «первая в списке» и «самая свежая» — догадки, которые сервер не обещал.
     */
    async listSessions(): Promise<readonly Session[]> {
      return unwrap(await send<DataEnvelope<Session[]>>(SESSIONS_PATH, 'GET'));
    },

    /**
     * Завершение сессии — любой, включая текущую.
     *
     * Чужая отвечает `404`, и различать её от несуществующей клиенту нечем:
     * это и есть нераскрытие существования чужого объекта.
     */
    async terminateSession(sessionId: string): Promise<void> {
      await send<unknown>(`${SESSIONS_PATH}/${encodeURIComponent(sessionId)}`, 'DELETE');
    },

    /**
     * Код на второй канал связи.
     *
     * Форма тела — та же, что у запроса кода входа: ключ канала ровно один, и
     * какой именно, выбирает клиент. Проверок формата здесь нет ни одной.
     */
    async requestChannelCode(input: AuthCodeInput): Promise<void> {
      await send<unknown>(CHANNEL_CODE_PATH, 'POST', input);
    },

    /**
     * Подтверждение второго канала.
     *
     * Наружу идёт **обновлённый профиль из ответа**: перерисовка по введённому
     * значению показала бы то, чего сервер, возможно, не принял.
     */
    async confirmChannel(input: ChannelConfirmInput): Promise<CurrentUserData> {
      return unwrap(await send<DataEnvelope<CurrentUserData>>(CHANNEL_CONFIRM_PATH, 'POST', input));
    },

    /**
     * Приглашение в фирму: роль и ровно один канал.
     *
     * Профиля приглашённому это не создаёт — приглашение адресовано каналу, а
     * вторую половину выполняет сам приглашённый своим токеном.
     */
    async createInvitation(companyId: string, input: InvitationInput): Promise<Invitation> {
      const path = `${companyPath(companyId)}${INVITATIONS_SEGMENT}`;

      return unwrap(await send<DataEnvelope<Invitation>>(path, 'POST', input));
    },

    /** Приглашения фирмы во всех статусах: отбора по статусу здесь нет. */
    async listCompanyInvitations(companyId: string): Promise<readonly Invitation[]> {
      const path = `${companyPath(companyId)}${INVITATIONS_SEGMENT}`;

      return unwrap(await send<DataEnvelope<Invitation[]>>(path, 'GET'));
    },

    /**
     * Отзыв приглашения — `204` **без тела**, как и у соседей того же рода.
     *
     * Развернуть конверт здесь нечего: `readPayload` на пустом теле отдаёт
     * `null`, и `unwrap(null)` бросил бы отказ на **удавшейся** операции —
     * сервер отозвал, а экран ушёл бы в состояние отказа целиком. Ряд один:
     * `terminateSession`, `revokeMemberGrant` и `revokeMemberDeny` объявлены
     * так же и `unwrap` не зовут.
     */
    async revokeInvitation(companyId: string, invitationId: string): Promise<void> {
      const path = `${companyPath(companyId)}${INVITATIONS_SEGMENT}/${encodeURIComponent(invitationId)}`;

      await send<unknown>(path, 'DELETE');
    },

    async resendInvitation(companyId: string, invitationId: string): Promise<Invitation> {
      const path = `${companyPath(companyId)}${INVITATIONS_SEGMENT}/${encodeURIComponent(invitationId)}/resend`;

      return unwrap(await send<DataEnvelope<Invitation>>(path, 'POST'));
    },

    /**
     * Слои-источники прав записи членства, как их отдал сервер.
     *
     * Действующих прав в ответе нет намеренно: их вычисляет сервер, и плоский
     * список задал бы неверную модель. Клиент не складывает слои ни здесь, ни
     * на экране.
     */
    async listMemberPermissions(companyId: string, membershipId: string): Promise<MemberPermissions> {
      const path = memberRightsPath(companyId, membershipId, '');

      return unwrap(await send<DataEnvelope<MemberPermissions>>(path, 'GET'));
    },

    async grantMemberPermission(companyId: string, membershipId: string, right: Permission): Promise<MemberPermissions> {
      const path = memberRightsPath(companyId, membershipId, '/grants');

      return unwrap(
        await send<DataEnvelope<MemberPermissions>>(path, 'POST', { permission: right }),
      );
    },

    async revokeMemberGrant(companyId: string, membershipId: string, right: Permission): Promise<void> {
      const path = memberRightsPath(companyId, membershipId, `/grants/${encodeURIComponent(right)}`);

      await send<unknown>(path, 'DELETE');
    },

    async denyMemberPermission(companyId: string, membershipId: string, right: Permission): Promise<MemberPermissions> {
      const path = memberRightsPath(companyId, membershipId, '/denies');

      return unwrap(
        await send<DataEnvelope<MemberPermissions>>(path, 'POST', { permission: right }),
      );
    },

    async revokeMemberDeny(companyId: string, membershipId: string, right: Permission): Promise<void> {
      const path = memberRightsPath(companyId, membershipId, `/denies/${encodeURIComponent(right)}`);

      await send<unknown>(path, 'DELETE');
    },

    /** Документы фирмы. Статуса самой фирмы в ответе нет — его не существует. */
    async listCompanyDocuments(companyId: string): Promise<readonly Document[]> {
      const path = `${companyPath(companyId)}${DOCUMENTS_SEGMENT}`;

      return unwrap(await send<DataEnvelope<Document[]>>(path, 'GET'));
    },

    /** Документы записи членства — той, что дана адресом, и никакой другой. */
    async listMemberDocuments(companyId: string, membershipId: string): Promise<readonly Document[]> {
      const path = `${membershipPath(companyId, membershipId)}${DOCUMENTS_SEGMENT}`;

      return unwrap(await send<DataEnvelope<Document[]>>(path, 'GET'));
    },

    /**
     * Временная подписанная ссылка на файл документа.
     *
     * Ссылка просится в момент нажатия и не кэшируется: каждый вызов даёт свою
     * со своим сроком, а годность решает сервер — `expires_at` клиент ни с чем
     * не сравнивает.
     */
    async issueDocumentLink(documentId: string): Promise<DocumentLink> {
      const path = `${DOCUMENTS_PATH}/${encodeURIComponent(documentId)}/link`;

      return unwrap(await send<DataEnvelope<DocumentLink>>(path, 'POST'));
    },

    /**
     * Реестр транспорта фирмы — как отдал сервер, включая выведенные записи.
     *
     * Ни отбора, ни сортировки — по состоянию в том числе: «машина в ремонте
     * на рейс не назначается» есть серверный guard, а не вид списка.
     */
    async listCompanyVehicles(companyId: string): Promise<readonly Vehicle[]> {
      const path = `${companyPath(companyId)}${VEHICLES_SEGMENT}`;

      return unwrap(await send<DataEnvelope<Vehicle[]>>(path, 'GET'));
    },

    /**
     * Добавление транспорта. Ответ — запись целиком: `201` — создана, `200` —
     * повтор своего VIN вернул уже существующую, и решил это сервер.
     */
    async addCompanyVehicle(companyId: string, input: VehicleCreateRequest): Promise<Vehicle> {
      const path = `${companyPath(companyId)}${VEHICLES_SEGMENT}`;

      return unwrap(await send<DataEnvelope<Vehicle>>(path, 'POST', input));
    },

    /**
     * Вывод из реестра — `204` **без тела**, разворачивать нечего: ряд тот же,
     * что `terminateSession` и `revokeInvitation`. Повтор вывода законен.
     */
    async withdrawCompanyVehicle(companyId: string, vehicleId: string): Promise<void> {
      await send<unknown>(vehiclePath(companyId, vehicleId), 'DELETE');
    },

    /**
     * Перевод состояния цикла ремонта: одно поле тела, ответ — вся запись.
     *
     * Допустимости перехода клиент не предсказывает: перескок через шаг и
     * операция над выведенным транспортом отклоняются сервером статусом 422.
     */
    async changeVehicleRepairState(
      companyId: string,
      vehicleId: string,
      input: RepairStateChangeRequest,
    ): Promise<Vehicle> {
      const path = `${vehiclePath(companyId, vehicleId)}${REPAIR_STATE_SEGMENT}`;

      return unwrap(await send<DataEnvelope<Vehicle>>(path, 'POST', input));
    },

    /** Журнал работ — в порядке ответа, без правки и удаления: операций нет. */
    async listVehicleServiceRecords(
      companyId: string,
      vehicleId: string,
    ): Promise<readonly ServiceRecord[]> {
      const path = `${vehiclePath(companyId, vehicleId)}${SERVICE_RECORDS_SEGMENT}`;

      return unwrap(await send<DataEnvelope<ServiceRecord[]>>(path, 'GET'));
    },

    /**
     * Сервисная запись с заголовком идемпотентности.
     *
     * Ключ рождается на **каждую** отправку заново: две одинаковые записи
     * законны, и подавлять вторую клиенту нечем — естественного ключа у
     * операции нет, решает заголовок.
     */
    async addVehicleServiceRecord(
      companyId: string,
      vehicleId: string,
      input: ServiceRecordCreateRequest,
    ): Promise<ServiceRecord> {
      const path = `${vehiclePath(companyId, vehicleId)}${SERVICE_RECORDS_SEGMENT}`;

      return unwrap(
        await send<DataEnvelope<ServiceRecord>>(path, 'POST', input, {
          [IDEMPOTENCY_HEADER]: newIdempotencyKey(),
        }),
      );
    },

    /** Заявки обеих сторон одним списком — сторона приходит полем ответа. */
    async listCompanyVinClaims(companyId: string): Promise<readonly VinClaim[]> {
      const path = `${companyPath(companyId)}${VIN_CLAIMS_SEGMENT}`;

      return unwrap(await send<DataEnvelope<VinClaim[]>>(path, 'GET'));
    },

    /**
     * Подача заявки: ровно два поля тела — VIN и реквизит техпаспорта.
     *
     * Поля файла нет: контракт загрузку выражать умеет и здесь не выражает,
     * недостача — решение автора контракта, а не пропуск клиента.
     */
    async createVinClaim(companyId: string, input: VinClaimCreateRequest): Promise<VinClaim> {
      const path = `${companyPath(companyId)}${VIN_CLAIMS_SEGMENT}`;

      return unwrap(await send<DataEnvelope<VinClaim>>(path, 'POST', input));
    },

    /**
     * Возражение держателя — без тела: заявку называет адрес. Окно считает
     * сервер: возражение после окна отклоняется статусом 422 от него.
     */
    async objectVinClaim(companyId: string, claimId: string): Promise<VinClaim> {
      const path = `${vinClaimPath(companyId, claimId)}${OBJECTION_SEGMENT}`;

      return unwrap(await send<DataEnvelope<VinClaim>>(path, 'POST'));
    },

    /**
     * Просмотр по ссылке: токен — в пути, кодированным; PIN — **только** телом
     * из одного поля. В адресе PIN оказался бы в журналах посредников и в
     * истории браузера, а тело туда не попадает.
     */
    async viewPublicTracking(linkToken: string, pin: string): Promise<PublicTracking> {
      const path = `${PUBLIC_TRACKING_PATH}/${encodeURIComponent(linkToken)}`;

      return unwrap(await send<DataEnvelope<PublicTracking>>(path, 'POST', { pin }));
    },

    /** Подсказка городов: строка поиска уходит как введена, годность судит сервер. */
    async listCities(query: { q: string }): Promise<readonly City[]> {
      const path = `${CITIES_PATH}?${new URLSearchParams({ q: query.q }).toString()}`;

      return unwrap(await send<DataEnvelope<City[]>>(path, 'GET'));
    },

    /** Справочник кузовов в порядке ответа: названия — на языке ответа. */
    async listBodyTypes(): Promise<readonly BodyTypeInfo[]> {
      return unwrap(await send<DataEnvelope<BodyTypeInfo[]>>(BODY_TYPES_PATH, 'GET'));
    },

    /**
     * Публикация объявления с ключом идемпотентности формы: повтор до ответа
     * несёт тот же ключ, и сервер не заводит второго объявления.
     */
    async createCargoListing(
      companyId: string,
      input: CargoListingSubmission,
      idempotencyKey: string,
    ): Promise<CargoListing> {
      const path = `${companyPath(companyId)}${CARGO_LISTINGS_SEGMENT}`;

      return unwrap(
        await send<DataEnvelope<CargoListing>>(path, 'POST', input, {
          [IDEMPOTENCY_HEADER]: idempotencyKey,
        }),
      );
    },

    /** Страница объявлений фирмы: ни отбора, ни пересортировки — порядок сервера. */
    async listCompanyCargoListings(
      companyId: string,
      options?: { cursor?: string },
    ): Promise<PageEnvelope<CargoListing>> {
      const path = withCursor(`${companyPath(companyId)}${CARGO_LISTINGS_SEGMENT}`, options?.cursor);

      return await send<PageEnvelope<CargoListing>>(path, 'GET');
    },

    async getCargoListing(listingId: string): Promise<CargoListing> {
      return unwrap(await send<DataEnvelope<CargoListing>>(cargoListingPath(listingId), 'GET'));
    },

    /** Правка целиком: та же форма тела; допустимость правки решает сервер (`409`). */
    async updateCargoListing(listingId: string, input: CargoListingSubmission): Promise<CargoListing> {
      return unwrap(await send<DataEnvelope<CargoListing>>(cargoListingPath(listingId), 'PUT', input));
    },

    async closeCargoListing(listingId: string): Promise<CargoListing> {
      const path = `${cargoListingPath(listingId)}/close`;

      return unwrap(await send<DataEnvelope<CargoListing>>(path, 'POST'));
    },

    /** Страница offer'ов объявления в порядке сервера, истёкшие и отклонённые тоже. */
    async listCargoListingOffers(
      listingId: string,
      options?: { cursor?: string },
    ): Promise<PageEnvelope<CargoOffer>> {
      const path = withCursor(`${cargoListingPath(listingId)}${OFFERS_SEGMENT}`, options?.cursor);

      return await send<PageEnvelope<CargoOffer>>(path, 'GET');
    },

    async acceptCargoOffer(offerId: string): Promise<CargoOffer> {
      const path = `${cargoOfferPath(offerId)}/accept`;

      return unwrap(await send<DataEnvelope<CargoOffer>>(path, 'POST'));
    },

    /** Отклонение без тела: адрес называет offer, причины форма не берёт. */
    async declineCargoOffer(offerId: string): Promise<CargoOffer> {
      const path = `${cargoOfferPath(offerId)}/decline`;

      return unwrap(await send<DataEnvelope<CargoOffer>>(path, 'POST'));
    },

    async listCargoSlots(listingId: string): Promise<readonly CargoSlot[]> {
      const path = `${cargoListingPath(listingId)}${SLOTS_SEGMENT}`;

      return unwrap(await send<DataEnvelope<CargoSlot[]>>(path, 'GET'));
    },
  };
}
